import { fetchElevationSegments } from './elevation';
import { formatDistance, formatDuration } from './formatters';
import { haversineKm } from './geomath';

const SITE = 'https://motoroam.app';
const CREATOR = `Motoroam (${SITE})`;

function escapeXml(str) {
  return String(str ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

// Spread the ≤100 sampled elevation points across every track coordinate.
// The sampler steps evenly through coordinate indices, so a coordinate at
// fraction i/(n-1) maps to sample fraction · (m-1) — lerp between neighbours.
function interpolateElevations(coords, points) {
  const n = coords.length;
  const m = points?.length || 0;
  if (m < 1) return null;
  const out = new Array(n);
  for (let i = 0; i < n; i++) {
    const t = m === 1 ? 0 : (i / (n - 1)) * (m - 1);
    const lo = Math.floor(t);
    const hi = Math.min(lo + 1, m - 1);
    const f = t - lo;
    out[i] = Math.round(points[lo].ele * (1 - f) + points[hi].ele * f);
  }
  return out;
}

function nearestEle(lat, lng, coords, coordEle) {
  if (!coordEle) return null;
  let best = Infinity, ele = null;
  for (let i = 0; i < coords.length; i++) {
    const dLat = coords[i][1] - lat;
    const dLng = coords[i][0] - lng;
    const d = dLat * dLat + dLng * dLng;
    if (d < best) { best = d; ele = coordEle[i]; }
  }
  return ele;
}

// Garmin/TomTom-recognised symbols + a readable type per waypoint role.
function waypointRole(wp, i, total) {
  if (i === 0) return { type: 'Start', sym: 'Flag, Green' };
  if (wp.isLoopEnd || i === total - 1) return { type: 'Finish', sym: 'Flag, Red' };
  if (wp.isLoopVia) return { type: 'Via', sym: 'Waypoint' };
  return { type: 'Stop', sym: 'Flag, Blue' };
}

/**
 * Build a GPX 1.1 document for a route.
 * @param {object} route { name, waypoints, geometry, elevation?, distance?, duration? }
 * @param {object} [opts] { rich } — when true (and elevation present) bakes
 *        per-point <ele>, device-ready waypoint symbols, bounds + stats.
 */
export function buildGPX(route, { rich = false } = {}) {
  const { name, waypoints = [], geometry, elevation, distance, duration } = route;

  const coords = geometry?.coordinates || waypoints.map(wp => [wp.lng, wp.lat]);
  const coordEle = rich && elevation ? interpolateElevations(coords, elevation.points) : null;

  // Waypoints — names always; symbols/types/ele only in rich mode.
  const wptNodes = waypoints
    .map((wp, i) => {
      const label = wp.name || `${wp.lat.toFixed(5)}, ${wp.lng.toFixed(5)}`;
      if (!rich) {
        return `  <wpt lat="${wp.lat}" lon="${wp.lng}">\n    <name>${escapeXml(label)}</name>\n  </wpt>`;
      }
      const { type, sym } = waypointRole(wp, i, waypoints.length);
      const ele = nearestEle(wp.lat, wp.lng, coords, coordEle);
      // GPX wpt child order: ele, name, sym, type.
      const eleNode = ele != null ? `\n    <ele>${ele}</ele>` : '';
      return `  <wpt lat="${wp.lat}" lon="${wp.lng}">${eleNode}\n    <name>${escapeXml(label)}</name>\n    <sym>${sym}</sym>\n    <type>${type}</type>\n  </wpt>`;
    })
    .join('\n');

  // Track points — bake <ele> when we have it.
  const trkptNodes = coords
    .map(([lon, lat], i) => {
      const ele = coordEle ? coordEle[i] : null;
      return ele != null
        ? `      <trkpt lat="${lat}" lon="${lon}"><ele>${ele}</ele></trkpt>`
        : `      <trkpt lat="${lat}" lon="${lon}"/>`;
    })
    .join('\n');

  // Rich metadata: a stats description + back-link (before <time>) and bounds
  // (after <time>) — GPX metadata child order is name, desc, link, time, bounds.
  let metaBeforeTime = '';
  let metaAfterTime = '';
  let trkDesc = '';
  if (rich) {
    const lats = coords.map(c => c[1]);
    const lons = coords.map(c => c[0]);
    metaAfterTime = `\n    <bounds minlat="${Math.min(...lats)}" minlon="${Math.min(...lons)}" maxlat="${Math.max(...lats)}" maxlon="${Math.max(...lons)}"/>`;
    const parts = [];
    if (distance != null) parts.push(formatDistance(distance));
    if (duration != null) parts.push(`~${formatDuration(duration)}`);
    if (elevation?.ascent != null) parts.push(`↑ ${elevation.ascent} m`);
    if (elevation?.descent != null) parts.push(`↓ ${elevation.descent} m`);
    const desc = ['Motoroam route', ...parts].join(' · ');
    metaBeforeTime = `\n    <desc>${escapeXml(desc)}</desc>\n    <link href="${SITE}"><text>Motoroam</text></link>`;
    trkDesc = `\n    <desc>${escapeXml(desc)}</desc>\n    <type>Motorcycle</type>`;
  }

  return `<?xml version="1.0" encoding="UTF-8"?>
<gpx version="1.1" creator="${escapeXml(CREATOR)}"
  xmlns="http://www.topografix.com/GPX/1/1"
  xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"
  xsi:schemaLocation="http://www.topografix.com/GPX/1/1 http://www.topografix.com/GPX/1/1/gpx.xsd">
  <metadata>
    <name>${escapeXml(name)}</name>${metaBeforeTime}
    <time>${new Date().toISOString()}</time>${metaAfterTime}
  </metadata>
${wptNodes}
  <trk>
    <name>${escapeXml(name)}</name>${trkDesc}
    <trkseg>
${trkptNodes}
    </trkseg>
  </trk>
</gpx>`;
}

/**
 * Download a route as GPX. In rich mode, fetches elevation (served from the
 * shared cache) if it isn't already attached, then bakes it into the file.
 */
export async function downloadGPX(route, { rich = false } = {}) {
  let r = route;
  if (rich && !route.elevation && route.geometry?.coordinates?.length >= 3) {
    try {
      const elevation = await fetchElevationSegments(route.geometry.coordinates);
      if (elevation) r = { ...route, elevation };
    } catch { /* fall back to a file without elevation */ }
  }

  const content = buildGPX(r, { rich });
  const blob = new Blob([content], { type: 'application/gpx+xml' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${(route.name || 'route').replace(/[^\w\s-]/g, '').trim().replace(/\s+/g, '_') || 'route'}.gpx`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Parse an imported GPX file into a route object ({ name, waypoints, geometry,
 * distance, duration }) that can be dropped straight into route state as
 * `current`. The imported track is a fixed, already road-following polyline —
 * it isn't re-routed through the engine — so only its first/last points
 * become waypoints; everything in between lives in `geometry` only.
 */
export function parseGPX(text, fallbackName = 'Imported Route') {
  const doc = new DOMParser().parseFromString(text, 'application/xml');
  if (doc.querySelector('parsererror')) {
    throw new Error('That file isn’t a valid GPX file.');
  }

  const trkpts = Array.from(doc.querySelectorAll('trk trkpt'));
  const pts = trkpts.length ? trkpts : Array.from(doc.querySelectorAll('rte rtept'));

  const coords = [];
  const times = [];
  for (const pt of pts) {
    const lat = parseFloat(pt.getAttribute('lat'));
    const lng = parseFloat(pt.getAttribute('lon'));
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue;
    coords.push([lng, lat]);
    const timeText = pt.querySelector('time')?.textContent;
    times.push(timeText ? Date.parse(timeText) : NaN);
  }
  if (coords.length < 2) {
    throw new Error('No track found in that GPX file.');
  }

  let distance = 0;
  for (let i = 1; i < coords.length; i++) {
    distance += haversineKm(coords[i - 1][1], coords[i - 1][0], coords[i][1], coords[i][0]) * 1000;
  }

  const firstTime = times[0];
  const lastTime = times[times.length - 1];
  const duration = Number.isFinite(firstTime) && Number.isFinite(lastTime) && lastTime > firstTime
    ? (lastTime - firstTime) / 1000
    : null;

  const name =
    doc.querySelector('gpx > metadata > name')?.textContent?.trim() ||
    doc.querySelector('trk > name')?.textContent?.trim() ||
    fallbackName;

  const [slng, slat] = coords[0];
  const [elng, elat] = coords[coords.length - 1];

  return {
    name,
    waypoints: [
      { lat: slat, lng: slng, name: 'Start' },
      { lat: elat, lng: elng, name: 'Finish' },
    ],
    geometry: { type: 'LineString', coordinates: coords },
    distance,
    duration,
  };
}
